import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DayTemplatePreview from "@/components/shifts/shared/DayTemplatePreview";
import { CompactShiftStatusBadge } from "@/components/shifts/status/ShiftStatusBadge";
import { ShiftStatus } from "@/lib/scheduling/shift-status-types";
import type { DayTemplateShift } from "@/types";
import { format } from "date-fns";

interface DayTemplatePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shifts: DayTemplateShift[];
  date: Date;
}

/**
 * Expandable modal for viewing shift preview in detail
 * Read-only view showing all employees and their shifts
 */
export function DayTemplatePreviewModal({
  open,
  onOpenChange,
  shifts,
  date
}: DayTemplatePreviewModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            Shift Details - {format(date, 'EEEE, MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Expanded preview with wider timeline */}
          <div className="border rounded-lg p-4 bg-muted/20">
            <DayTemplatePreview shifts={shifts} width={800} />
          </div>

          {/* List view of shifts with details */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">All Shifts ({shifts.length})</h3>
            <div className="space-y-1 text-sm">
              {shifts.map((shift, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/30">
                  <span className="font-mono text-xs text-muted-foreground">
                    {shift.startTime} - {shift.endTime}
                  </span>
                  <span className="flex-1">{shift.title}</span>
                  {shift.status && (
                    <CompactShiftStatusBadge
                      status={shift.status as ShiftStatus}
                      showTooltip={false}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
